import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import JobCard from "../components/JobCard";
import RecommendedRow from "../components/RecommendedRow";
import { ArrowRight, Search, MapPin, Sparkles, TrendingUp, Hammer } from "lucide-react";

const INDUSTRY_META = {
  "Construction": { blurb: "Build America", span: "md:col-span-2 md:row-span-2" },
  "Healthcare": { blurb: "Care & heal", span: "md:col-span-1" },
  "Technology": { blurb: "Ship code", span: "md:col-span-1" },
  "Advanced Manufacturing": { blurb: "Make things", span: "md:col-span-2" },
  "Energy": { blurb: "Power the grid", span: "md:col-span-1" },
  "Transportation": { blurb: "Move the world", span: "md:col-span-1" },
  "Financial Services": { blurb: "Money moves", span: "md:col-span-1" },
  "Hospitality": { blurb: "Serve guests", span: "md:col-span-1" },
  "Telecommunications": { blurb: "Connect people", span: "md:col-span-1" },
  "Education": { blurb: "Teach & lead", span: "md:col-span-1" },
  "Agriculture": { blurb: "Feed the nation", span: "md:col-span-1" },
};

export default function Home() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const [location, setLocation] = useState("");
  const [industries, setIndustries] = useState([]);
  const [trending, setTrending] = useState([]);
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/industries").then((r) => setIndustries(r.data));
    api.get("/jobs/trending").then((r) => setTrending(r.data));
    api.get("/jobs?limit=6").then((r) => setFeatured(r.data.jobs));
  }, []);

  const doSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (location) params.set("location", location);
    navigate(`/jobs?${params.toString()}`);
  };

  return (
    <div className="relative">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-zinc-800">
        <div className="absolute inset-0 grid-lines opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-br from-black via-zinc-950 to-black" />
        <img
          src="https://images.unsplash.com/photo-1672748341520-6a839e6c05bb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwxfHxjb25zdHJ1Y3Rpb24lMjB3b3JrZXIlMjBwb3J0cmFpdHxlbnwwfHx8fDE3ODUyNjU5MjR8MA&ixlib=rb-4.1.0&q=85"
          alt=""
          className="absolute right-0 top-0 h-full w-1/2 object-cover opacity-30 hidden lg:block"
          style={{ maskImage: "linear-gradient(to left, black 40%, transparent)" }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <div className="max-w-4xl">
            <div className="flex items-center gap-2 text-xs font-mono-alt uppercase tracking-widest text-yellow-500 mb-6">
              <Hammer size={14} /> US Registered Apprenticeship &middot; 40 Jobs Live
            </div>
            <h1 className="font-display uppercase leading-[0.85] text-white text-6xl sm:text-7xl md:text-8xl lg:text-9xl">
              Earn <span className="text-yellow-500">while</span><br />
              you <span className="text-yellow-500">learn.</span>
            </h1>
            <p className="mt-8 text-lg md:text-xl text-zinc-400 max-w-2xl leading-relaxed">
              Skip the debt. Start your career with a paid Registered Apprenticeship — from electricians to software engineers, powered by U.S. Department of Labor data.
            </p>

            {/* Search */}
            <form onSubmit={doSearch} className="mt-10 flex flex-col md:flex-row gap-2 max-w-3xl relative corner-accents p-3 bg-black/70 border border-zinc-800">
              <div className="flex-1 flex items-center gap-2 px-3 border-r border-zinc-800">
                <Search size={16} className="text-yellow-500" />
                <input
                  data-testid="home-search-keyword"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Occupation, company, or keyword"
                  className="flex-1 bg-transparent py-3 text-white placeholder:text-zinc-600 focus:outline-none"
                />
              </div>
              <div className="flex-1 flex items-center gap-2 px-3">
                <MapPin size={16} className="text-yellow-500" />
                <input
                  data-testid="home-search-location"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="City, State, or Zip"
                  className="flex-1 bg-transparent py-3 text-white placeholder:text-zinc-600 focus:outline-none"
                />
              </div>
              <button
                data-testid="home-search-submit"
                className="bg-yellow-500 text-black font-bold uppercase tracking-wider px-8 py-3 hover:bg-yellow-400 active:scale-95 transition flex items-center gap-2 justify-center"
              >
                Find Jobs <ArrowRight size={16} />
              </button>
            </form>

            <div className="mt-6 flex flex-wrap gap-2">
              {["Electrician", "HVAC", "Software Developer", "Welder", "Nurse"].map((t) => (
                <button
                  key={t}
                  data-testid={`home-quick-${t}`}
                  onClick={() => navigate(`/jobs?q=${encodeURIComponent(t)}`)}
                  className="text-xs uppercase tracking-wider text-zinc-500 hover:text-yellow-500 border border-zinc-800 hover:border-yellow-500 px-3 py-1 transition"
                >
                  {t}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* RECOMMENDED FOR YOU (auth-gated) */}
      {user && <RecommendedRow />}

      {/* AI MATCH TEASER */}
      <section className="border-b border-zinc-800 py-16 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="text-xs font-mono-alt uppercase text-yellow-500 tracking-widest mb-4 flex items-center gap-2">
              <Sparkles size={14} /> AI Career Matching
            </div>
            <h2 className="font-display text-5xl md:text-6xl uppercase leading-none">
              Not sure where <br />to <span className="text-yellow-500">start</span>?
            </h2>
            <p className="mt-6 text-zinc-400 leading-relaxed max-w-xl">
              Paste your skills, past jobs, or resume. Our AI matches you to the top 5 Registered Apprenticeships in seconds — with reasoning for every match.
            </p>
            <Link
              to="/ai-match"
              data-testid="home-ai-match-cta"
              className="mt-8 inline-flex items-center gap-2 border border-yellow-500 text-yellow-500 hover:bg-yellow-500 hover:text-black font-bold uppercase tracking-wider px-6 py-3 transition"
            >
              Try AI Match <ArrowRight size={16} />
            </Link>
          </div>
          <div className="relative border border-zinc-800 bg-zinc-950 p-6 overflow-hidden">
            <div className="scan-line" />
            <div className="font-mono-alt text-xs text-zinc-500 space-y-2">
              <div><span className="text-yellow-500">&gt;</span> analyzing.skills[]</div>
              <div className="pl-4 text-zinc-400">"welding", "blueprint reading", "steel"</div>
              <div><span className="text-yellow-500">&gt;</span> scanning 40 apprenticeships&hellip;</div>
              <div className="pl-4"><span className="text-green-500">✓</span> Structural Ironworker (94% match)</div>
              <div className="pl-4"><span className="text-green-500">✓</span> Sheet Metal Worker (89% match)</div>
              <div className="pl-4"><span className="text-green-500">✓</span> Welding Apprentice (87% match)</div>
              <div><span className="text-yellow-500">&gt;</span> return top_matches</div>
            </div>
          </div>
        </div>
      </section>

      {/* INDUSTRIES BENTO */}
      <section className="py-20 border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="text-xs font-mono-alt uppercase text-yellow-500 tracking-widest mb-2">Industries</div>
              <h2 className="font-display text-5xl md:text-6xl uppercase leading-none">Pick your <span className="text-yellow-500">path</span></h2>
            </div>
            <Link to="/industries" className="hidden md:flex text-sm uppercase tracking-wider text-zinc-500 hover:text-white items-center gap-2">
              All Industries <ArrowRight size={14} />
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 auto-rows-[140px] md:auto-rows-[160px] gap-2">
            {industries.slice(0, 8).map((ind, i) => {
              const meta = INDUSTRY_META[ind.name] || {};
              return (
                <Link
                  key={ind.name}
                  to={`/jobs?industry=${encodeURIComponent(ind.name)}`}
                  data-testid={`industry-${ind.name}`}
                  className={`group border border-zinc-800 hover:border-yellow-500 bg-zinc-950 p-5 flex flex-col justify-between transition-all ${meta.span || ""}`}
                  style={{ animation: `fadeUp 0.5s ease ${i * 60}ms both` }}
                >
                  <div className="text-xs font-mono-alt uppercase tracking-widest text-zinc-500 group-hover:text-yellow-500">
                    #{String(i + 1).padStart(2, "0")}
                  </div>
                  <div>
                    <div className="font-display text-2xl md:text-3xl uppercase leading-none group-hover:text-yellow-500 transition-colors">{ind.name}</div>
                    <div className="text-xs text-zinc-500 mt-1 tracking-wider uppercase">{ind.count} jobs &middot; {meta.blurb || "Explore"}</div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* TRENDING OCCUPATIONS */}
      <section className="py-20 border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 text-xs font-mono-alt uppercase text-yellow-500 tracking-widest mb-2">
            <TrendingUp size={14} /> Nationwide Trending
          </div>
          <h2 className="font-display text-5xl md:text-6xl uppercase leading-none mb-10">
            Hot <span className="text-yellow-500">Occupations</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
            {trending.map((t, i) => (
              <Link
                key={t.onet_code}
                to={`/jobs?occupation=${encodeURIComponent(t.occupation)}`}
                data-testid={`trending-${t.onet_code}`}
                className="group border border-zinc-800 hover:border-yellow-500 bg-zinc-950 p-5 flex justify-between items-start transition-all"
                style={{ animation: `fadeUp 0.5s ease ${i * 60}ms both` }}
              >
                <div>
                  <div className="font-display text-xl uppercase leading-tight group-hover:text-yellow-500 transition-colors">{t.occupation}</div>
                  <div className="text-xs text-zinc-600 font-mono-alt mt-2">O*NET {t.onet_code}</div>
                </div>
                <div className="text-yellow-500 font-mono-alt text-sm">{t.count}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED JOBS */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="text-xs font-mono-alt uppercase text-yellow-500 tracking-widest mb-2">Latest</div>
              <h2 className="font-display text-5xl md:text-6xl uppercase leading-none">Fresh <span className="text-yellow-500">gigs</span></h2>
            </div>
            <Link to="/jobs" data-testid="home-view-all" className="text-sm uppercase tracking-wider text-zinc-500 hover:text-white flex items-center gap-2">
              View All <ArrowRight size={14} />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {featured.map((j, i) => (<JobCard key={j.id} job={j} index={i} />))}
          </div>
        </div>
      </section>
    </div>
  );
}
